import clsx from "clsx";
import { isEqual } from "lodash-es";
import type { FC } from "react";
import { useEffect, useState } from "react";

import {
  OutcomeAssessmentType,
  OutcomeType,
  RubricDescriptionType,
  RubricType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { CancelSaveWidget } from "mds/components/CancelSaveWidget";
import { BaseTextArea } from "mds/components/TextArea";
import { TextField } from "mds/components/TextField";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { PencilEditIcon, ScoreIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi } from "store/index";
import { NestedPageOutcomeType, NestedSectionOutcomeType } from "store/selectors";
import { scoreDescriptionBackgroundColor } from "utils/outcome";
import { FeatureStatus, usePresentation } from "utils/presentation";

interface RubricActionBarProps {
  canEdit: boolean;
  isEditing: boolean;
  outcomeItem: NestedPageOutcomeType | NestedSectionOutcomeType | OutcomeType;

  /**
   * The descriptions that the user is currently editing.
   */
  editedRubricDescriptions: string[];

  /**
   * The descriptions that are displayed when the user is not in edit mode.
   */
  actualRubricDescriptions: string[];
  setIsEditing: (isEditing: boolean) => void;
}

/**
 * An action bar for the rubric that includes a reset button and an edit button.
 *
 * When the edit button is clicked, the user can edit the rubric description. The edit button turns into a
 * Cancel / Save widget so that the descriptions can be saved once the user is done editing.
 *
 * The reset button is only shown if the user is not editing the rubric descriptions and the outcome item's
 * "rubric" field is not null. NOTE: This "rubric" field on the outcome item should NOT be used for display.
 * When it is null, this means that the rubric descriptions are inherited from another outcome item, and we
 * should use our selectors to determine which rubric descriptions to display. */
const RubricActionBar: FC<RubricActionBarProps> = ({
  outcomeItem,
  isEditing,
  setIsEditing,
  canEdit,
  editedRubricDescriptions,
  actualRubricDescriptions,
}) => {
  const modelType =
    "outcome" in outcomeItem && "section_outcome" in outcomeItem
      ? "page_outcomes"
      : "outcome" in outcomeItem
        ? "section_outcomes"
        : "outcomes";
  const outcome = "outcome" in outcomeItem ? outcomeItem.outcome : outcomeItem;

  const onRubricReset = () => {
    storeApi[modelType].partial_update({
      id: outcomeItem.id,
      rubric_descriptions: null,
    });
  };

  const onSaveDescriptions = (newRubricDescriptions: RubricDescriptionType[]) => {
    storeApi[modelType].partial_update({
      id: outcomeItem.id,
      rubric_descriptions: newRubricDescriptions,
    });
  };

  const onSave = () => {
    setIsEditing(false);
    const trimmedRubricDescriptions = editedRubricDescriptions.map((description) =>
      (description || "").trim(),
    );
    if (canEdit && !isEqual(trimmedRubricDescriptions, actualRubricDescriptions)) {
      onSaveDescriptions(trimmedRubricDescriptions);
    }
  };

  return canEdit ? (
    <div className="flex gap-1">
      {/* TODO: Add a tooltip for this reset button. */}
      {!isEditing && outcomeItem.rubric_descriptions && (
        <SquareButton kind="ghost" size="xs" onClick={onRubricReset}>
          {t("rubric_list.reset_rubric")}
        </SquareButton>
      )}
      {!isEditing ? (
        <SquareIconButton
          eventName="Page Outcome - Edit rubric"
          eventProps={{ outcome: outcome.title }}
          kind="ghost"
          size="s"
          title={t("tooltip.edit_rubric")}
          onClick={() => setIsEditing(true)}
        >
          <PencilEditIcon />
        </SquareIconButton>
      ) : (
        <CancelSaveWidget
          saveEventName="Page Outcome - Save rubric"
          saveEventProps={{ outcome: outcome.title }}
          size="xs"
          onClickCancel={() => setIsEditing(false)}
          onClickSave={onSave}
        />
      )}
    </div>
  ) : null;
};

interface RubricListProps {
  className?: string;
  outcomeItem: NestedPageOutcomeType | NestedSectionOutcomeType | OutcomeType;
  readOnly?: boolean;

  /**
   * The full rubric for this outcome item. Not to be confused with the "rubric" field on an outcome item, which is
   * a list of rubric descriptions. This "rubric" prop should include any inherited rubric descriptions. See
   * selectPageOutcomeRubric, selectSectionOutcomeRubric, and selectOrgOutcomeRubric selectors for more details.
   *
   * A page outcome item may inherit its rubric descriptions from a course outcome item or an org outcome item.
   * A course outcome item may inherit its rubric descriptions from an org outcome item.
   * An org outcome item may inherit its rubric descriptions from the default rubric descriptions.
   */
  rubric: RubricType;

  /**
   * Whether to show the "Rubric" title with the RubricActionBar. Defaults to true. If false, the RubricActionBar
   * will be shown next to the first rubric item.
   */
  showHeader?: boolean;
  outcomeAssessment?: OutcomeAssessmentType;
}

export const RubricList: FC<RubricListProps> = ({
  className = "",
  outcomeItem,
  readOnly,
  rubric,
  outcomeAssessment,
  showHeader = true,
}) => {
  const rubricClassName = clsx("w-full", className);

  const { canAuthorSection } = useCapabilities();

  const canEdit = !readOnly && canAuthorSection;
  const actualRubricDescriptions = rubric?.map((rubricItem) => rubricItem.description);
  const { featureStatus } = usePresentation();
  const showScoreIds = featureStatus === FeatureStatus.OUTCOME;

  const [isEditing, setIsEditing] = useState(false);
  const [rubricDescriptions, setRubricDescriptions] = useState(actualRubricDescriptions);

  useEffect(() => {
    if (!isEditing) {
      setRubricDescriptions(rubric?.map((rubricItem) => rubricItem.description));
    }
  }, [isEditing, rubric]);

  const onDescriptionChange = (newValue: string, index: number) => {
    const newRubricDescriptions = [...rubricDescriptions];
    newRubricDescriptions[index] = newValue;
    setRubricDescriptions(newRubricDescriptions);
  };

  const onDoubleClick = () => {
    if (canEdit) {
      setIsEditing(true);
    }
  };

  if (!rubric || !rubricDescriptions) return null;

  return (
    <div className={rubricClassName}>
      {showHeader && (
        <div className="flex items-center justify-between pb-2">
          <div className="h3">{t("glossary.rubric")}</div>
          <RubricActionBar
            actualRubricDescriptions={actualRubricDescriptions}
            canEdit={canEdit}
            editedRubricDescriptions={rubricDescriptions}
            isEditing={isEditing}
            outcomeItem={outcomeItem}
            setIsEditing={setIsEditing}
          />
        </div>
      )}

      {rubric.map((rubricLevel, index) => (
        <div
          className={clsx(
            "flex h-full w-full flex-col rounded px-1 py-2",
            rubricLevel.score === outcomeAssessment?.score &&
              scoreDescriptionBackgroundColor(outcomeAssessment?.score),
          )}
          key={rubricLevel.score}
        >
          <div className="flex items-center justify-between">
            <div
              className="flex items-center gap-2 px-[2px] py-1"
              id={showScoreIds ? `outcome-${outcomeItem.id}-${rubricLevel.score}` : undefined}
            >
              <ScoreIcon value={rubricLevel.score} />
              <TextField size="h4" value={rubricLevel.name} readOnly />
            </div>

            {index === 0 && !showHeader && canEdit && (
              <RubricActionBar
                actualRubricDescriptions={actualRubricDescriptions}
                canEdit={canEdit}
                editedRubricDescriptions={rubricDescriptions}
                isEditing={isEditing}
                outcomeItem={outcomeItem}
                setIsEditing={setIsEditing}
              />
            )}
          </div>

          <div className="flex h-full pb-1 pl-6 pr-1">
            <BaseTextArea
              className="pl-0 text-sm"
              divProps={{ onDoubleClick }}
              readOnly={!canEdit || !isEditing}
              value={rubricDescriptions[index]}
              onChange={(e) => isEditing && onDescriptionChange(e.target.value, index)}
              onDoubleClick={onDoubleClick}
            />
          </div>
        </div>
      ))}
    </div>
  );
};
